import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
    DeviceInfoRow,
    EstimateDetail,
    EstimateInquiry,
    EstimatePattern,
    ESTIMATE_DETAIL_INIT,
    INIT_DEVICE_INFO_INPUT_ROW,
    INIT_ESTIMATE_INQUIRY,
    INIT_ESTIMATE_PATTERN,
} from '../types/common/EstimateInquiryType';

// 条件管理
const slice = createSlice({
    name: 'EstimateInquiry',
    // 初期値
    initialState: INIT_ESTIMATE_INQUIRY,
    reducers: {
        // 全置換
        setState: (_state, action: PayloadAction<EstimateInquiry>) => action.payload,
        // 機器情報追加(設置場所情報はadd時には1つのみ前提)
        addDeviceRow: (state: EstimateInquiry, action: PayloadAction<DeviceInfoRow>) => {
            // 行No採番
            let nextDeviceRowNumber = 1;
            if (state.deviceList.length > 0) {
                nextDeviceRowNumber = Math.max(...state.deviceList.map((row) => row.deviceRowNumber)) + 1;
            }
            // 機器対象を生成
            state.deviceList.push({
                ...action.payload,
                deviceRowNumber: nextDeviceRowNumber,
                inputList: [
                    {
                        ...INIT_DEVICE_INFO_INPUT_ROW,
                        ...(action.payload.inputList?.length > 0 ? action.payload.inputList[0] : {}),
                        deviceRowNumber: nextDeviceRowNumber,
                        inputRowNumber: 1, // 新規行なので1固定
                    },
                ],
            });
            // 見積り条件を更新、新規機器を追加
            state.estimatePatternList.forEach((it) => {
                it.estimateList.push({
                    ...ESTIMATE_DETAIL_INIT,
                    deviceRowNumber: nextDeviceRowNumber,
                    inputRowNumber: 1, // 新規行なので1固定
                });
                it.checkAllFlag = it.estimateList.find((it) => !it.checkFlag) === undefined ? true : false;
            });
        },
        // 機器情報（全体）を削除
        deleteDeviceRow: (state, delDeviceRowNumber: PayloadAction<number>): EstimateInquiry => {
            const deviceList = state.deviceList.filter((row) => row.deviceRowNumber !== delDeviceRowNumber.payload);
            const estimatePatternList = state.estimatePatternList.map((pattern) => {
                const changedEstimateList = pattern.estimateList.filter((estimate) => {
                    return estimate.deviceRowNumber !== delDeviceRowNumber.payload;
                });
                let checkAllFlag = pattern.checkAllFlag;
                // 機器対象が0件の場合、全選択を外す
                if (changedEstimateList.length === 0) {
                    checkAllFlag = false;
                } else {
                    checkAllFlag = changedEstimateList.find((it) => !it.checkFlag) === undefined ? true : false;
                }
                return {
                    ...pattern,
                    estimateList: changedEstimateList,
                    checkAllFlag: checkAllFlag,
                };
            });
            return {
                deviceList: deviceList,
                estimatePatternList: estimatePatternList,
            };
        },
        // 見積り条件を追加
        addEstimatePattern: (state, estimateList: PayloadAction<Array<EstimateDetail>>): EstimateInquiry => {
            // 採番処理（現在の条件件数＋１）
            let nextPatternRowNum = 1;
            if (state.estimatePatternList.length > 0) {
                nextPatternRowNum =
                    Math.max(...state.estimatePatternList.map((item) => item.estimatePatternRowNumber)) + 1;
            }
            // 見積り条件対象を新規
            const changedEstimatePatternList: Array<EstimatePattern> = [
                ...state.estimatePatternList,
                {
                    ...INIT_ESTIMATE_PATTERN,
                    estimatePatternRowNumber: nextPatternRowNum,
                    estimateList: estimateList.payload,
                },
            ];
            const changedEstimateInquiry = {
                ...state,
                estimatePatternList: changedEstimatePatternList,
            };
            return changedEstimateInquiry;
        },
        // 見積り条件を削除
        delEstimatePattern: (state, delEstimatePatternRowNumber: PayloadAction<number>): EstimateInquiry => {
            const changedEstimatePatternList = state.estimatePatternList.filter(
                (pattern) => pattern.estimatePatternRowNumber !== delEstimatePatternRowNumber.payload
            );
            const changedEstimateInquiry = {
                ...state,
                estimatePatternList: changedEstimatePatternList,
            };
            return changedEstimateInquiry;
        },
        // 初期化
        initState: () => INIT_ESTIMATE_INQUIRY,
    },
});

export const { actions, reducer } = slice;
export const { setState, initState, addDeviceRow, deleteDeviceRow, addEstimatePattern, delEstimatePattern } = actions;
